const FREE_VERSION_CHARACTER_COUNT = 10000;

export const EXPANDED_DESKTOP_NAVBAR_WIDTH = 240;
export const EXPANDED_TABLET_NAVBAR_WIDTH = 300;
export const NAVBAR_WIDTH = 145;
export const DESKTOP_WIDTH_BREAKPOINT = 1193;

export const promptText =
  '\nPrompt: Rephrase the following input text into 3 concrete and varied statements which can be verified. Avoid opinions which can’t be proven. The claim should express a strong viewpoint or assertion about the subject, maintaining the language of the input text. Keep the claims fairly short and concise. Example Inputs and Outputs:\nInput: "climate change"\nOutput: "Climate change is caused by humans, "The sun causes climate change.", "Poor countries are more susceptible to climate change."\nInput: "artificial intelligence"\nOutput: "Artificial intelligence will surpass human intelligence within a decade.", "Artificial intelligence was invented in the 60s", "John McCarthy is one of the founding fathers of artificial intelligence", "Artificial intelligence requires immense amounts of electricity."\nInput: "Norge\'s bevolkning er det største" Output: "Norge har det største antallet mennesker i verden.", "Norge har det minste antallet mennesker i verden."\nInput:';
export const wordsCount = (str) => {
  return str.trim().split(/\s+/).length;
};

export const charCount = (str: string) => str.trim().length;

export const isTooLongText = (searchValue: string) =>
  charCount(searchValue) > FREE_VERSION_CHARACTER_COUNT;

export enum SourceDates {
  MOST_RECENT = 'MOST_RECENT',
  LEAST_RECENT = 'LEAST_RECENT',
}

export enum ErrorCodes {
  TIMEOUT = 'TIMEOUT',
  GENERIC_ERROR = 'GENERIC_ERROR',
}

export enum PublishedSinceDates {
  ANY = '0',
  THREE_MONTHS = '3',
  SIX_MONTHS = '6',
  ONE_YEAR = '12',
  TWO_YEARS = '24',
}

export type SearchEngine = {
  name: string;
  label?: string;
  tooltip?: string;
};

export const searchEngines: SearchEngine[] = [
  { name: 'Google' },
  { name: 'Google Claim' },
  { name: 'You.com' },
  { name: 'Bing Search' },
  { name: 'Wikipedia' },
  { name: 'Semantic Scholar' },
  {
    name: 'ElasticSearch',
    label: 'FactiSearch',
    tooltip:
      'FactiSearch is the worlds largest database of fact-checks, tracking fact-checks in more than 90 languages.',
  },
  {
    name: 'SSB',
    label: 'Statistisk Sentralbyrå',
    tooltip: "Norway's statistics bureau",
  },
  {
    name: 'SNL',
    label: 'Store Norske Leksikon',
    tooltip: 'Norwegian Encyclopedia',
  },
];

export enum SourceTypes {
  SUPPORTING = 'supporting',
  MIXED = 'mixed',
  DISPUTING = 'disputing',
  ALL = 'all',
  NONE = 'none',
}

export enum ClaimTypes {
  SUPPORTED = 'supported',
  DISPUTED = 'disputed',
  UNVERIFIED = 'unverified',
}

enum SourceLabelDescription {
  SUPPORTS = 'SUPPORTS',
  DISPUTES = 'DISPUTES',
}
export const sourceTypeValues = [SourceTypes.DISPUTING, SourceTypes.MIXED, SourceTypes.SUPPORTING];

export const getErrorText = (searchValue: string) => {
  if (charCount(searchValue) > FREE_VERSION_CHARACTER_COUNT) {
    return 'Input exceeds 10000 characters, please analyze a shorter text.';
  }
};

export function areArraysEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) return false;

  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();
  return sortedArr1.every((value, index) => value === sortedArr2[index]);
}

export const shuffleArray = (array) => {
  return array
    .map((item) => ({ item, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map(({ item }) => item);
};

export const sortSourcesByDate = (sources: Source[], selectedSourceDate: SourceDates) => {
  const sourcesCopy = [...sources];
  const nullOldDate = new Date(70, 0, 1);
  const nullNewDate = new Date();
  if (selectedSourceDate === null) return sources;
  else if (selectedSourceDate === SourceDates.MOST_RECENT) {
    return sourcesCopy?.sort(
      (a, b) =>
        new Date(b.publishDate ?? nullOldDate).getTime() -
        new Date(a.publishDate ?? nullOldDate).getTime()
    );
  } else if (selectedSourceDate === SourceDates.LEAST_RECENT) {
    return sourcesCopy?.sort(
      (a, b) =>
        new Date(a.publishDate ?? nullNewDate).getTime() -
        new Date(b.publishDate ?? nullNewDate).getTime()
    );
  }
};

export const extractSummaryTextFromClaim = (claim: Claim) => {
  const { claim: claimText, evidence, summary, finalPrediction } = claim;

  const supportingSources =
    evidence?.filter((e) => e.labelDescription === SourceLabelDescription.SUPPORTS).length || 0;
  const disputingSources =
    evidence?.filter((e) => e.labelDescription === SourceLabelDescription.DISPUTES).length || 0;
  const mixedSources = evidence && evidence.length - (supportingSources + disputingSources);

  let summaryText = `Claim: ${claimText}\nSupporting sources: ${supportingSources}, Mixed sources: ${mixedSources}, Disputing sources: ${disputingSources}`;

  if (summary?.length) {
    summaryText += `\n\nThe sources ${
      finalPrediction === 1 ? 'support' : 'dispute'
    } this claim:\n\n${summary.map((summaryItem) => `- ${summaryItem}`).join('\n')}`;
  } else {
    summaryText += `\n\nNo summary available.`;
  }

  if (evidence && evidence.length > 0) {
    summaryText += `\n\nWe have found evidence from:\n\n${evidence
      .map((evidenceItem) => `[${evidenceItem.title}](${evidenceItem.url})`)
      .join('\n\n')}`;
  }

  return summaryText;
};

export const handleCopyToClipboard = async (textToCopy) => {
  try {
    await navigator.clipboard.writeText(textToCopy);
    return true;
  } catch (err) {
    console.error('Failed to copy text: ', err);
    return false;
  }
};

export function containsInteger(str) {
  const integerRegex = /\b\d+\b/;
  return integerRegex.test(str);
}
